import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Mappa gerarchica: per ogni pattern di URL restituisce il parent logico.
 * Ordine: dalla più specifica alla più generica.
 */
const PARENT_MAP: Array<{ pattern: RegExp; parent: string }> = [
  // Prenotazioni
  { pattern: /^\/prenotazioni\/lista\/nuovo$/, parent: "/prenotazioni/lista" },
  { pattern: /^\/prenotazioni\/lista\/[^/]+$/, parent: "/prenotazioni/lista" },
  { pattern: /^\/prenotazioni\/nuovo$/, parent: "/prenotazioni/lista" },
  { pattern: /^\/prenotazioni\/lista$/, parent: "/" },
  { pattern: /^\/prenotazioni\/calendario$/, parent: "/" },
  { pattern: /^\/prenotazioni\/ical$/, parent: "/" },
  { pattern: /^\/prenotazioni\/ospiti\/nuovo$/, parent: "/ospiti/database" },
  { pattern: /^\/prenotazioni\/ospiti\/[^/]+$/, parent: "/ospiti/database" },
  { pattern: /^\/prenotazioni\/ospiti$/, parent: "/ospiti/database" },
  { pattern: /^\/prenotazioni$/, parent: "/" },

  // Strutture
  { pattern: /^\/strutture\/camere\/[^/]+$/, parent: "/strutture/camere" },
  { pattern: /^\/strutture\/camere$/, parent: "/strutture/lista" },
  { pattern: /^\/strutture\/lista$/, parent: "/" },
  { pattern: /^\/strutture\/[^/]+$/, parent: "/strutture/lista" },

  // Ospiti
  { pattern: /^\/ospiti\/database$/, parent: "/" },
  { pattern: /^\/ospiti\/[^/]+$/, parent: "/ospiti/database" },

  // Contratti
  { pattern: /^\/contratti\/[^/]+$/, parent: "/contratti" },
  { pattern: /^\/contratti$/, parent: "/" },

  // Team
  { pattern: /^\/team\/host\/[^/]+$/, parent: "/team/host" },
  { pattern: /^\/team\/host$/, parent: "/" },
  { pattern: /^\/team\/collaboratori\/[^/]+$/, parent: "/team/collaboratori" },
  { pattern: /^\/team\/collaboratori$/, parent: "/" },
  { pattern: /^\/team\/manutenzione\/[^/]+$/, parent: "/team/manutenzione" },
  { pattern: /^\/team\/manutenzione$/, parent: "/" },
  { pattern: /^\/team\/pulizie$/, parent: "/" },

  // Operativo
  { pattern: /^\/operativo\/attivita\/[^/]+$/, parent: "/operativo/attivita" },
  { pattern: /^\/operativo\/attivita$/, parent: "/" },
  { pattern: /^\/operativo\/segnalazioni\/[^/]+$/, parent: "/operativo/segnalazioni" },
  { pattern: /^\/operativo\/segnalazioni$/, parent: "/" },
  { pattern: /^\/operativo\/recensioni\/[^/]+$/, parent: "/operativo/recensioni" },
  { pattern: /^\/operativo\/recensioni$/, parent: "/" },

  // Finanze
  { pattern: /^\/finanze\/transazioni\/[^/]+$/, parent: "/finanze/transazioni" },
  { pattern: /^\/finanze\/transazioni$/, parent: "/finanze/panoramica" },
  { pattern: /^\/finanze\/panoramica$/, parent: "/" },
  { pattern: /^\/finanze\/entrate$/, parent: "/" },
  { pattern: /^\/finanze\/uscite$/, parent: "/" },
  { pattern: /^\/finanze\/report$/, parent: "/" },
  { pattern: /^\/finanze$/, parent: "/" },

  // Guest App
  { pattern: /^\/guest-app\/[^/]+$/, parent: "/" },

  // Impostazioni
  { pattern: /^\/impostazioni$/, parent: "/" },

  // Pricing
  { pattern: /^\/pricing$/, parent: "/" },
];

export function getParentPath(pathname: string): string {
  for (const { pattern, parent } of PARENT_MAP) {
    if (pattern.test(pathname)) return parent;
  }
  return "/";
}

/** Pulsante back UI — restituisce una funzione da chiamare onClick */
export function useParentPath() {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    navigate(getParentPath(location.pathname));
  };

  return goBack;
}

/**
 * Intercetta il tasto fisico "indietro" del browser.
 * Va montato una sola volta nel Layout.
 */
export function useBackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Mantieni sempre un entry "sentinel" sopra quello corrente
    // così il popstate scatta quando si preme indietro
    window.history.pushState({ hedita: true }, "");

    const handlePop = () => {
      const parent = getParentPath(location.pathname);
      // Ri-pushiamo subito il sentinel per il prossimo press
      window.history.pushState({ hedita: true }, "");
      navigate(parent, { replace: true });
    };

    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [location.pathname]);
}
